import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Switch, Select, Space } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import api from '../../utils/axios';

const { Title } = Typography;
const { Option } = Select;

interface CompanySettings {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  features: {
    twoFactorAuth: boolean;
    auditLogging: boolean;
    apiAccess: boolean;
  };
}

const CompanySettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: CompanySettings }>('/settings/company');
      if (response.data.success) {
        form.setFieldsValue(response.data.data);
      } else {
        message.error('Failed to fetch company settings');
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
      message.error('Failed to fetch company settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (values: CompanySettings) => {
    setSaving(true);
    try {
      const response = await api.put('/settings/company', values);
      if (response.data.success) {
        message.success('Settings saved successfully');
      } else {
        message.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Company Settings</Title>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={saving}
          >
            Save Changes
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          disabled={loading}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Title level={5}>Basic Information</Title>
              <Form.Item
                name="name"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Please enter address' }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </div>

            <div>
              <Title level={5}>Preferences</Title>
              <Form.Item
                name="timezone"
                label="Timezone"
                rules={[{ required: true, message: 'Please select timezone' }]}
              >
                <Select>
                  <Option value="UTC">UTC</Option>
                  <Option value="America/New_York">Eastern Time</Option>
                  <Option value="America/Chicago">Central Time</Option>
                  <Option value="America/Denver">Mountain Time</Option>
                  <Option value="America/Los_Angeles">Pacific Time</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="dateFormat"
                label="Date Format"
                rules={[{ required: true, message: 'Please select date format' }]}
              >
                <Select>
                  <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                  <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                  <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: 'Please select currency' }]}
              >
                <Select>
                  <Option value="USD">USD ($)</Option>
                  <Option value="EUR">EUR (€)</Option>
                  <Option value="GBP">GBP (£)</Option>
                  <Option value="CAD">CAD ($)</Option>
                </Select>
              </Form.Item>
            </div>

            <div>
              <Title level={5}>Notifications</Title>
              <Form.Item
                name={['notifications', 'email']}
                label="Email Notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={['notifications', 'sms']}
                label="SMS Notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={['notifications', 'push']}
                label="Push Notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>

            <div>
              <Title level={5}>Features</Title>
              <Form.Item
                name={['features', 'twoFactorAuth']}
                label="Two-Factor Authentication"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={['features', 'auditLogging']}
                label="Audit Logging"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={['features', 'apiAccess']}
                label="API Access"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CompanySettings; 